import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

// Grupos/clientes com limite de unidades contratadas (somente administrador geral).
export function GruposPanel({ meta, onChanged }) {
  const [form, setForm] = useState(null);
  const nomePousada = (id) => meta.pousadas.find((p) => p.id === id)?.nome || id;

  const salvar = async () => {
    try {
      const body = { nome: form.nome, limite_unidades: Number(form.limite_unidades || 0), pousada_ids: form.pousada_ids };
      if (form.id) await api.patch(`/acessos/grupos/${form.id}`, body);
      else await api.post("/acessos/grupos", body);
      toast.success("Grupo salvo");
      setForm(null);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao salvar grupo"); }
  };
  const excede = form && form.pousada_ids.length > Number(form.limite_unidades || 0);

  return (
    <section className="bg-white border border-stone-200 rounded-lg p-5 mt-8" data-testid="grupos-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-lg text-stone-900">Grupos e clientes</h2>
          <p className="text-xs text-stone-500">Unidades contratadas por conta administrativa.</p>
        </div>
        <Button size="sm" variant="outline" data-testid="btn-novo-grupo"
          onClick={() => setForm({ nome: "", limite_unidades: 1, pousada_ids: [] })}><Plus size={14} className="mr-1" />Novo grupo</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {meta.grupos.map((g) => (
          <button key={g.id} type="button" data-testid={`grupo-card-${g.id}`} onClick={() => setForm({ ...g })}
            className="text-left border border-stone-200 rounded-md p-3 hover:bg-stone-50 transition-colors">
            <div className="flex items-center gap-2 text-sm font-medium"><Building2 size={14} />{g.nome}</div>
            <div className="text-xs text-stone-500 mt-1">{g.pousada_ids.length} de {g.limite_unidades} unidade(s){!g.ativo && " · inativo"}</div>
            {g.pendente_regularizacao && (
              <div className="text-[11px] text-red-700 mt-1" data-testid={`grupo-pendente-${g.id}`}>
                Acima do limite contratado — pendente de regularização (acesso operacional do administrador do grupo bloqueado)
              </div>
            )}
            <div className="text-xs text-stone-600 mt-1 line-clamp-2">{g.pousada_ids.map(nomePousada).join(", ") || "Sem unidades"}</div>
          </button>
        ))}
        {!meta.grupos.length && <div className="text-sm text-stone-500">Nenhum grupo cadastrado.</div>}
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="grupo-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Editar grupo" : "Novo grupo"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Nome do grupo/cliente *</Label>
                <Input value={form.nome} data-testid="grupo-input-nome" onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>Limite de unidades contratadas *</Label>
                <Input type="number" min="1" value={form.limite_unidades} data-testid="grupo-input-limite"
                  onChange={(e) => setForm({ ...form, limite_unidades: e.target.value })} /></div>
              <div><Label>Unidades ({form.pousada_ids.length})</Label>
                <div className="max-h-48 overflow-y-auto border border-stone-200 rounded-md p-3 space-y-2">
                  {meta.pousadas.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.pousada_ids.includes(p.id)} data-testid={`grupo-unidade-${p.id}`}
                        onCheckedChange={() => setForm({ ...form, pousada_ids: form.pousada_ids.includes(p.id)
                          ? form.pousada_ids.filter((x) => x !== p.id) : [...form.pousada_ids, p.id] })} />
                      {p.nome}
                    </label>
                  ))}
                </div>
                {excede && <p className="text-xs text-red-700 mt-1" data-testid="grupo-aviso-limite">Unidades acima do limite contratado.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={salvar} disabled={excede} data-testid="grupo-btn-salvar" className="bg-[#083A6B] hover:bg-[#062C52]">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
